// Billing page — current plan summary, upgrade tiers, one-time top-ups,
// and a portal link for existing paid subscribers.
//
// Every "Select" and "Manage" button is a full-page navigation to
// Polar (via our backend) — Polar's checkout UI is the source of truth
// for card entry, tax, receipts, etc. We never handle PANs on our side.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink, Sparkles } from "lucide-react";
import {
  createCheckout,
  getBillingCatalog,
  openPortal,
  type BillingCatalogItem,
} from "../lib/api";
import { useBalance } from "../lib/useBalance";
import { ACTION_COSTS, planLabel, PLANS as PLAN_META } from "../lib/credits";
import { cn } from "../lib/cn";

export default function Billing() {
  const navigate = useNavigate();
  const { balance, isLoading: balLoading, error: balError } = useBalance();
  const catalog = useQuery({
    queryKey: ["billing-catalog"],
    queryFn: getBillingCatalog,
  });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const selectItem = async (
    item: BillingCatalogItem,
    kind: "plan" | "topup",
  ) => {
    if (!item.polar_product_id) {
      setCheckoutError(
        "This product isn't fully provisioned yet. Check POLAR_PRODUCT_* env vars.",
      );
      return;
    }
    setCheckoutError(null);
    setPendingId(item.id);

    // Plan changes on an already-subscribed account go through Polar's
    // customer portal, not a fresh checkout. Polar rejects a new
    // subscription checkout with "You already have an active
    // subscription" when one exists. Top-ups are one-time — always use
    // checkout.
    if (kind === "plan" && hasPaidPlan) {
      try {
        const url = await openPortal();
        window.location.href = url;
      } catch (e) {
        setCheckoutError((e as Error).message);
        setPendingId(null);
      }
      return;
    }

    try {
      const { url } = await createCheckout(item.polar_product_id);
      window.location.href = url;
    } catch (e) {
      setCheckoutError((e as Error).message);
      setPendingId(null);
    }
  };

  const openManagePortal = async () => {
    setCheckoutError(null);
    try {
      const url = await openPortal();
      window.location.href = url;
    } catch (e) {
      setCheckoutError((e as Error).message);
    }
  };

  const currentPlanId = balance?.plan_id ?? "free";
  const currentPlanMeta = PLAN_META[currentPlanId];
  const hasPaidPlan = currentPlanId !== "free";

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-10">
      <header>
        <div className="eyebrow">Billing</div>
        <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
          Credits &amp; plans
        </h1>
        <p className="text-sm text-fg-muted mt-2 max-w-2xl">
          Credits never expire and roll over month to month.
        </p>
      </header>

      {checkoutError && (
        <div className="surface p-4 text-sm text-danger border-danger/40 bg-danger/10">
          {checkoutError}
        </div>
      )}

      {/* ─── What credits buy ─────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          What credits buy
        </h2>
        <div className="surface overflow-hidden">
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 divide-border/60 [&>*:nth-child(odd)]:md:border-r [&>*:nth-child(odd)]:md:border-border/60 [&>*:nth-child(n+3)]:md:border-t [&>*:nth-child(n+3)]:md:border-border/60">
            <CostRow label="Build a deck" cost={ACTION_COSTS.build_deck} hint="60-card list + sideboard, grounded in tournament data" />
            <CostRow label="Upload & analyze a deck" cost={ACTION_COSTS.upload_deck} hint="Parse, validate, and LLM-analyze your own list" />
            <CostRow label="Ask about a deck" cost={ACTION_COSTS.ask_deck} hint="Conversational follow-up with card lookups" />
            <CostRow label="Match summary" cost={ACTION_COSTS.match_summary} hint="LLM recap of a completed match (cached, so re-opens are free)" />
          </div>
          <div className="border-t border-border/60">
            <CostRow label="Playtest match" cost={ACTION_COSTS.playtest} hint="Forge-simulated games — always free, run as many as you want" />
          </div>
          <p className="text-[11px] text-fg-subtle px-5 py-3 border-t border-border/60">
            Costs are fixed regardless of prompt length. Failed actions are
            refunded automatically.
          </p>
        </div>
      </section>

      {/* ─── Current plan ───────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Current plan
        </h2>
        <div className="surface p-5">
          {balLoading && <div className="skeleton h-16 w-full" />}
          {balError && !balLoading && (
            <div className="text-sm text-danger">{balError.message}</div>
          )}
          {balance && (
            <div className="flex flex-wrap items-center gap-5">
              <div>
                <div className="text-xl font-semibold">
                  {planLabel(currentPlanId)}
                  {currentPlanMeta.price_usd === null ? (
                    <span className="text-fg-subtle text-sm font-normal ml-2">
                      Free
                    </span>
                  ) : (
                    <span className="text-fg-subtle text-sm font-normal ml-2">
                      ${currentPlanMeta.price_usd}/mo
                    </span>
                  )}
                </div>
                <div className="text-xs text-fg-muted mt-1">
                  {currentPlanMeta.monthly_grant.toLocaleString()} credits
                  per month · 1-month rollover
                </div>
              </div>
              <div className="pl-5 border-l border-border">
                <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
                  Balance
                </div>
                <div className="font-mono tabular-nums text-2xl mt-0.5">
                  {balance.total.toLocaleString()}
                </div>
                <div className="text-[11px] text-fg-subtle font-mono">
                  {balance.granted} granted · {balance.purchased} purchased
                </div>
              </div>
              {(hasPaidPlan || (balance && balance.purchased > 0)) && (
                <div className="ml-auto">
                  <button
                    className="btn-secondary text-sm"
                    onClick={openManagePortal}
                  >
                    <ExternalLink size={14} strokeWidth={1.75} />
                    {hasPaidPlan ? "Manage subscription" : "Billing portal"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ─── Upgrade tiers ─────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          {hasPaidPlan ? "Change plan" : "Upgrade"}
        </h2>
        {catalog.isLoading && <div className="skeleton h-40 w-full" />}
        {catalog.error && (
          <div className="surface p-4 text-sm text-danger">
            {(catalog.error as Error).message}
          </div>
        )}
        {catalog.data && (
          <div className="grid gap-4 md:grid-cols-3">
            {catalog.data.plans.map((p) => (
              <TierCard
                key={p.id}
                item={p}
                recommended={p.id === "pro"}
                currentPlan={currentPlanId === p.id}
                pending={pendingId === p.id}
                onSelect={() => void selectItem(p, "plan")}
                priceSuffix="/mo"
                ctaLabel={
                  currentPlanId === p.id
                    ? "Current"
                    : hasPaidPlan
                      ? "Change"
                      : "Select"
                }
              />
            ))}
          </div>
        )}
      </section>

      {/* ─── Top-ups ───────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Top-ups
        </h2>
        <p className="text-xs text-fg-muted max-w-2xl -mt-1">
          One-time credit packs. Never expire. Stacks on top of your
          monthly grant. No subscription required.
        </p>
        {catalog.data && (
          <div className="grid gap-4 md:grid-cols-3">
            {catalog.data.topups.map((t) => (
              <TierCard
                key={t.id}
                item={t}
                recommended={t.id === "topup-medium"}
                pending={pendingId === t.id}
                onSelect={() => void selectItem(t, "topup")}
                priceSuffix=" one-time"
                ctaLabel="Buy"
                bonusHint={bonusHint(t)}
              />
            ))}
          </div>
        )}
      </section>

      <div className="pt-4 border-t border-border">
        <button
          className="text-xs text-fg-subtle hover:text-fg transition-colors"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

function CostRow({
  label,
  cost,
  hint,
}: {
  label: string;
  cost: number;
  hint: string;
}) {
  const isFree = cost === 0;
  return (
    <div className="flex flex-col gap-1 p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span
          className={cn(
            "inline-flex items-center justify-center rounded-md px-2 py-0.5 font-mono tabular-nums text-xs border",
            isFree
              ? "bg-fg/5 text-fg-muted border-border"
              : "bg-accent/15 text-accent border-accent/30",
          )}
        >
          {isFree ? "Free" : cost}
        </span>
      </div>
      <div className="text-[11px] text-fg-subtle leading-snug">{hint}</div>
    </div>
  );
}

// Rough "extra credits" callout for top-up cards. Purely cosmetic — the
// credit amount on the card is already the final value user receives.
// Computed as delta vs the $5 / 200-credit baseline.
function bonusHint(t: BillingCatalogItem): string | null {
  const basePerDollar = 200 / 5; // 40 credits/$
  const actualPerDollar = t.credits / t.price_usd;
  const bonus = Math.round(
    ((actualPerDollar - basePerDollar) / basePerDollar) * 100,
  );
  if (bonus <= 0) return null;
  return `+${bonus}% bonus`;
}

function TierCard({
  item,
  recommended,
  currentPlan,
  pending,
  onSelect,
  priceSuffix,
  ctaLabel,
  bonusHint: bonus,
}: {
  item: BillingCatalogItem;
  recommended?: boolean;
  currentPlan?: boolean;
  pending: boolean;
  onSelect: () => void;
  priceSuffix: string;
  ctaLabel: string;
  bonusHint?: string | null;
}) {
  const disabled = currentPlan || pending || !item.polar_product_id;
  return (
    <div
      className={cn(
        "surface p-5 flex flex-col gap-3 relative",
        recommended && "border-accent/60 shadow-card-hover",
      )}
    >
      {recommended && (
        <div className="absolute -top-2.5 left-4 flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          <Sparkles size={10} strokeWidth={2} />
          Recommended
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-fg">{item.name}</div>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="font-mono tabular-nums text-2xl">
            ${item.price_usd}
          </span>
          <span className="text-xs text-fg-subtle">{priceSuffix}</span>
        </div>
      </div>
      <div className="text-sm text-fg-muted">
        <span className="font-mono tabular-nums text-fg">
          {item.credits.toLocaleString()}
        </span>{" "}
        credits
        {bonus && (
          <span className="ml-2 text-[11px] text-accent font-medium">
            {bonus}
          </span>
        )}
      </div>
      <button
        className={cn(
          currentPlan
            ? "btn-secondary text-sm opacity-60 cursor-default"
            : recommended
              ? "btn-primary text-sm"
              : "btn-secondary text-sm",
        )}
        disabled={disabled}
        onClick={onSelect}
      >
        {currentPlan && <Check size={14} strokeWidth={2} />}
        {pending ? "Redirecting…" : ctaLabel}
      </button>
      {!item.polar_product_id && (
        <div className="text-[10px] text-warn font-mono">
          Not provisioned — set POLAR_PRODUCT_*
        </div>
      )}
    </div>
  );
}
